import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import BlockchainBadge from "../components/BlockchainBadge";
import Analytics from "../components/Analytics";
import AdvancedAnalytics from "../components/AdvancedAnalytics";
import AdminChrome from "../components/AdminChrome";
import { useRequireAdmin } from "./AdminLogin";

export default function AdminDashboard() {
  useRequireAdmin();
  const nav = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [audit, setAudit] = useState([]);
  const [tab, setTab] = useState("posts");

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/posts", { params: { include_drafts: true, limit: 100 } });
      setPosts(data.items || []);
    } catch (e) {
      if ([401, 403].includes(e.response?.status)) nav("/admin/login", { replace: true });
    } finally {
      setLoading(false);
    }
  };

  const fetchAudit = async () => {
    try {
      const { data } = await api.get("/admin/audit", { params: { limit: 50 } });
      setAudit(data.items || []);
    } catch (e) { /* ignore */ }
  };

  const downloadAudit = async () => {
    try {
      const response = await api.get("/admin/audit/download", {
        params: { limit: 50 },
        responseType: "blob",
      });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = "audit-log.txt";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(e.response?.data?.detail || "audit download failed");
    }
  };

  useEffect(() => { fetchAll(); fetchAudit(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const publish = async (id) => {
    if (!window.confirm("Publish & timestamp this post on the Bitcoin blockchain?")) return;
    try {
      await api.post(`/posts/${id}/publish`);
      await fetchAll();
    } catch (e) { alert(e.response?.data?.detail || "publish failed"); }
  };
  const unpublish = async (id) => {
    try { await api.post(`/posts/${id}/unpublish`); await fetchAll(); }
    catch (e) { alert(e.response?.data?.detail || "error"); }
  };
  const del = async (id, title) => {
    if (!window.confirm(`Soft-delete "${title}"?\n\nContent will be removed but the timeline entry + blockchain proof will remain. Click OK to soft-delete, Cancel to keep.`)) return;
    try { await api.post(`/posts/${id}/soft-delete`); await fetchAll(); }
    catch (e) { alert(e.response?.data?.detail || "error"); }
  };
  const purge = async (id, title) => {
    if (!window.confirm(`Permanently DELETE "${title}"? This cannot be undone and removes the post entirely (including from the timeline).`)) return;
    try { await api.delete(`/posts/${id}`); await fetchAll(); }
    catch (e) { alert(e.response?.data?.detail || "error"); }
  };

  return (
    <div className="App">
      <Header />
      <main className="xs-page" data-testid="admin-dashboard">
        <div className="xs-container">
          <AdminChrome />
          <h1 style={{ margin: "0 0 4px", color: "#ffde16" }}>admin dashboard</h1>

          <div className="xs-admin-tabs">
            <button
              className={`xs-admin-tab ${tab === "posts" ? "active" : ""}`}
              onClick={() => setTab("posts")}
              data-testid="tab-posts"
            >posts ({posts.length})</button>
            <span className="xs-admin-sep" aria-hidden="true">|</span>
            <button
              className={`xs-admin-tab ${tab === "analytics" ? "active" : ""}`}
              onClick={() => setTab("analytics")}
              data-testid="tab-analytics"
            >analytics</button>
            <span className="xs-admin-sep" aria-hidden="true">|</span>
            <button
              className={`xs-admin-tab ${tab === "advanced" ? "active" : ""}`}
              onClick={() => setTab("advanced")}
              data-testid="tab-advanced"
            >advanced</button>
            <span className="xs-admin-sep" aria-hidden="true">|</span>
            <button
              className={`xs-admin-tab ${tab === "audit" ? "active" : ""}`}
              onClick={() => { setTab("audit"); fetchAudit(); }}
              data-testid="tab-audit"
            >audit-log</button>
          </div>

          {tab === "analytics" && <Analytics />}
          {tab === "advanced" && <AdvancedAnalytics />}

          {tab === "posts" && (
            <div data-testid="admin-posts-list">
              {loading && <div style={{ opacity: 0.6, padding: 24 }}>loading…</div>}
              {!loading && posts.length === 0 && (
                <div style={{ padding: 24, border: "1px dashed #ffde16", borderRadius: 8 }}>
                  no posts yet. click <strong>+ new post</strong> to create one.
                </div>
              )}
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {posts.map((p) => (
                  <div
                    key={p.id}
                    style={{
                      border: "1px solid #ffde16",
                      borderRadius: 8,
                      padding: "12px 16px",
                      background: "rgba(0,0,0,0.6)",
                      display: "grid",
                      gridTemplateColumns: "1fr auto",
                      gap: 12,
                      alignItems: "center",
                    }}
                    data-testid={`admin-post-${p.slug}`}
                  >
                    <div>
                      <div style={{ fontWeight: "bold", fontSize: "1.05em" }}>{p.title}</div>
                      <div style={{ fontSize: "0.8em", opacity: 0.75, marginTop: 4, display: "flex", gap: 10, flexWrap: "wrap" }}>
                        <span>{p.is_draft ? "DRAFT" : `published ${new Date(p.published_at).toLocaleDateString()}`}</span>
                        <span>/{p.slug}</span>
                        {p.audio_url && <span>♪</span>}
                        {p.video_url && <span>▶</span>}
                        <BlockchainBadge hash={p.blockchain_hash} status={p.opentimestamps_status} compact />
                        <span>{p.view_count || 0} views</span>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
                      {!p.is_draft && (
                        <Link
                          to={`/articles/${p.slug}`}
                          className="xs-btn ghost"
                          target="_blank"
                        >view ↗</Link>
                      )}
                      <Link to={`/admin/edit/${p.id}`} className="xs-btn ghost" data-testid={`edit-${p.slug}`}>edit</Link>
                      {p.is_draft ? (
                        <button className="xs-btn primary" onClick={() => publish(p.id)} data-testid={`publish-${p.slug}`}>publish</button>
                      ) : (
                        <button className="xs-btn ghost" onClick={() => unpublish(p.id)} data-testid={`unpublish-${p.slug}`}>unpublish</button>
                      )}
                      <button className="xs-btn danger" onClick={() => del(p.id, p.title)} data-testid={`delete-${p.slug}`}>soft-delete</button>
                      <button className="xs-btn danger" onClick={() => purge(p.id, p.title)} data-testid={`purge-${p.slug}`} title="Permanent delete (no timeline trace)">purge</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === "audit" && (
            <>
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
                <button type="button" className="xs-btn ghost" onClick={downloadAudit}>Download .txt</button>
              </div>
              <div
                style={{ fontFamily: "Courier New, monospace", fontSize: "0.85em", background: "rgba(0,0,0,0.7)", border: "1px solid #ffde16", borderRadius: 8, padding: 16, maxHeight: 500, overflow: "auto" }}
                data-testid="audit-log"
              >
              {audit.length === 0 ? (
                <div style={{ opacity: 0.6 }}>no audit events yet.</div>
              ) : (
                audit.map((a) => (
                  <div key={a.id} style={{ padding: "4px 0", borderBottom: "1px dashed rgba(255,222,22,0.15)" }}>
                    <span style={{ opacity: 0.7 }}>{a.timestamp}</span>{" "}
                    <strong>{a.action}</strong>{" "}
                    {a.username && <span>username={a.username} </span>}
                    <span style={{ opacity: 0.65 }}>ip={a.ip}</span>{" "}
                    {a.success !== undefined && <span>success={String(a.success)} </span>}
                    {(a.country || a.city) && <span>geo={[a.city, a.country].filter(Boolean).join(", ")} </span>}
                    {a.user_agent && <span style={{ opacity: 0.65 }}>ua={a.user_agent} </span>}
                    {a.details && Object.keys(a.details).length > 0 && (
                      <span style={{ opacity: 0.7 }}> — {JSON.stringify(a.details)}</span>
                    )}
                  </div>
                ))
              )}
              </div>
            </>
          )}
        </div>
      </main>
      <Footer show />
    </div>
  );
}
